import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { useI18nContext } from '@/i18n/i18n-react';
import type { TenantAppDownloadsChartResponseDto } from '@/generated/korza-api-client/types.gen';
import {
  bucketStartIso,
  compareIsoDateStrings,
  cumulativeAxisDomain,
  formatChartAxisLabel,
  type ChartGranularity,
} from '@/lib/chart-axis';
import { useChartTooltip } from '@/lib/hooks/use-chart-tooltip';
import { usePartialBuckets } from '@/lib/hooks/use-partial-buckets';
import { markPartialRows, type ChartRange } from '@/lib/partial-buckets';
import { MetricsAreaChart } from '@/components/ui/charts/area-chart';
import { MetricsChartAreaSeries } from '@/components/ui/charts/chart-area-series';

type PointDto = TenantAppDownloadsChartResponseDto['points'][number];

export type Granularity = ChartGranularity;

interface CumulativeDownloadsChartProps {
  granularity: Granularity;
  points: PointDto[];
  /** The filter's period: marks the partial first/last bucket (CHART-01). */
  range?: ChartRange;
  syncId?: string;
  syncMethod?: 'index' | 'value';
}

/**
 * Backend returns cumulative install counts per point; when merging into coarse buckets,
 * keep the latest point within each bucket (counts are monotone, summing would be wrong).
 * The Y axis starts near the smallest total (ANLT-06), with a note saying so.
 */
export function CumulativeDownloadsChart({
  granularity,
  points,
  range,
  syncId,
  syncMethod,
}: CumulativeDownloadsChartProps) {
  const { LL, locale } = useI18nContext();

  const rows = useMemo(() => {
    const sorted = [...points].sort((a, b) =>
      compareIsoDateStrings(a.date, b.date)
    );
    const merged = new Map<string, { iso: string; cumulative: number }>();
    for (const p of sorted) {
      const k =
        bucketStartIso(p.date, granularity, locale) ??
        (DateTime.fromISO(p.date).isValid ? p.date : null);
      if (!k) continue;
      const prev = merged.get(k);
      if (!prev || compareIsoDateStrings(p.date, prev.iso) >= 0) {
        merged.set(k, {
          iso: p.date,
          cumulative: p.downloadsCount,
        });
      }
    }
    return [...merged.entries()]
      .sort(([a], [b]) => compareIsoDateStrings(a, b))
      .map(([iso, row]) => ({
        month: formatChartAxisLabel(DateTime.fromISO(iso), granularity, locale),
        date: iso,
        cumulative: row.cumulative,
      }));
  }, [granularity, locale, points]);

  const partial = usePartialBuckets(rows, range, granularity);
  const chartData = useMemo(
    () => markPartialRows(rows, 'cumulative', partial.edges),
    [rows, partial.edges]
  );
  const yDomain = useMemo(() => {
    if (rows.length === 0) return undefined;
    const values = rows.map((row) => row.cumulative);
    return cumulativeAxisDomain(Math.min(...values), Math.max(...values));
  }, [rows]);

  const tooltipContent = useChartTooltip({
    formatRows: (_label, payload) => [
      {
        label: LL.metricsPage.charts.downloadsCumulative.title(),
        value: new Intl.NumberFormat(locale).format(
          (payload[0]?.value as number) ?? 0
        ),
      },
    ],
  });

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-1">
      <div className="min-h-0 flex-1">
        <MetricsAreaChart
          data={chartData}
          className="min-h-0"
          partialLegend={partial.edges}
          syncId={syncId}
          syncMethod={syncMethod}
          yAxisOrientation="right"
          yAxisWidth={44}
          yAxisDomain={yDomain}
          // The stacked series starts at 0: without this recharts widens the
          // domain back to 0 (and, with the shared syncId, froze the page).
          yAxisAllowDataOverflow
          tooltipContent={tooltipContent}
        >
          <MetricsChartAreaSeries
            dataKey="cumulative"
            name={LL.metricsPage.charts.downloadsCumulative.title()}
            partialDataKey="cumulativePartial"
            partialLabels={partial.labels}
          />
        </MetricsAreaChart>
      </div>
      {yDomain && yDomain[0] > 0 ? (
        <p className="text-xs text-muted-foreground">
          {LL.metricsPage.charts.downloadsCumulative.axisNote()}
        </p>
      ) : null}
    </div>
  );
}
